import { 
  Host,
  Input,
  Output,
  Optional,
  Directive,
  DestroyRef,
  HostBinding,
  EventEmitter,
  HostListener,
} from '@angular/core';
/**
 * enums
*/
import { IThemeColorVariant } from '@core/enums/theme-colors-variants';

/**
 * directives
 */
import { ListGroupDirective } from './list-group.directive';

@Directive({
  selector: '[appListGroupItem]',
  host: {
    '[class.list-group-item]': 'true',
  }
})
export class ListGroupItemDirective {

  /**
   * @event click
   * @description Handles item activation and emits change event
   */
  @HostListener('click', ['$event'])
  protected _handlerActiveItem(event: Event) {
    
    // void
    if (this.disabled) {
      event.stopPropagation();
      return;
    }
    
    // check if item is actionable to fire actions
    if (this.actionable) {
      
      // switch between one fire or toggle fire
      if (this.itemToggle && !this._host?.isTabpanel) this.toggle();
      else this.activeItem();
      
    }

    // emit event by current item
    this.activeItemChange.emit(this);

  }

  /**
   * @input Sets active state and applies aria-current
   */
  @HostBinding('class.active')
  @HostBinding('attr.aria-current')
  @Input({ alias: 'itemActive' })
  public active: boolean | null = null

  /**
   * @output Emits when active state changes
   */
  @Output('activeItemChange')
  public activeItemChange = new EventEmitter<this>();

  /**
   * @input Disables the item (both visually and behaviorally)
   */
  @HostBinding('attr.aria-disabled')
  @HostBinding('class.disabled')
  @Input({ alias: 'itemDisabled' })
  public disabled: boolean | null = null;

  /**
   * @input Enables actionable behavior (toggle, emit)
   */
  @HostBinding('class.list-group-item-action')
  @Input({ alias: 'itemActionable' })
  public actionable: boolean = false;

  /**
   * @input Applies timeline class variation
   */
  @Input({ alias: 'timeline' })
  public isTimeline: boolean = false;

  /**
   * @input Applies variant-based styling class
   */
  @Input({ alias: "variant" })
  public variant!: IThemeColorVariant


  /**
   * @input Provides a unique value to identify the item (for tabpanel use)
   */
  @Input({ alias: 'itemValue' })
  public itemValue!: number | string

  /**
   * @input Controls toggle behavior on click
   */
  @Input({ alias: 'itemToggle' })
  public itemToggle: boolean = false;  

  /**
   * @description Dynamically generates CSS classes for variant and layout
   */
  @HostBinding('class')
  private get _handlerVariantClass() {

    // mapped
    const variantKey = this.variant;
    
    // init
    const _baseClassName: string [] = ['list-group'];

    // push sub class by condition
    if (this.isTimeline) _baseClassName.push('timeline');
    else _baseClassName.push('item');
  
    return variantKey ? `${_baseClassName.join("-")}-${variantKey}` : null;

  }


  constructor(
    public destroyRef: DestroyRef,
    @Optional() @Host() private _host: ListGroupDirective,
  ) { }

  /**
   * @method activeItem
   * @description Activates the item visually
   */
  public activeItem(): void {
    
    // void
    if (this.active) return;

    this.active = true;

  }

  /**
   * @method inActiveItem
   * @description Deactivates the item visually
   */
  public inActiveItem(): void {

    // void
    if (!this.active) return;

    this.active = false;

  }

  /**
   * @method toggle
   * @description Toggles active/inactive state
   */
  public toggle(): void {
    !this.active ? this.activeItem() : this.inActiveItem();
  }

}
